import classnames from 'classnames';
import { ReactNode } from 'react';

import { ChevronDirection, ChevronIcon, LocationIcon, SuccessIcon } from '../Icon';
import { normalizeTestId } from '../../utils';
import { StepItem, StepListProps, StepStatus, SubStepItem, SubStepStatus } from './types';
import './StepList.css';
/*
   Summary overview for StepList component following CMS Design System patterns and customized 
   Displays a series of steps in a process or workflow
   Uses custom CSS styling instead of CMS Design System CSS
   CMS StepList only supports strings for title and description.
   CMS StepList does not render React elements like <h3> or <span> inside the title/description. That’s why your headings, numbers, and optional labels don’t appear, and your custom colors don’t work. You cannot pass JSX to CmsStepList and expect it to render. That’s a limitation of the CMS component.

   ACP-2076 (Section 508): step titles are items in a `role="list"`, not section
   headings, so they must not carry heading semantics — assistive technology would
   otherwise announce them in the page heading outline. They render as `span` rather
   than a block element because one branch nests the title inside a `button`, which
   only accepts phrasing content; `.step-list__title` carries `display: block` to
   keep the layout identical.
*/

export function StepList({
  steps,
  className = '',
  showNumbers = true,
  onStepClick,
  onSubStepClick,
  expandedStepIds,
  onToggleStep,
  allStepsClickable = false,
  showSubSubSteps = false,
  completedText = 'Completed',
  editText = 'Edit',
  resumeText = 'Resume',
  startText = 'Start',
  component,
  'data-testid': dataTestId,
}: StepListProps) {
  const baseTestId = dataTestId ?? 'step-list';
  const getStatusClass = (status: StepItem['status']): string => {
    switch (status) {
      case StepStatus.COMPLETED:
        return 'step-list__item--completed';
      case StepStatus.CURRENT:
        return 'step-list__item--current';
      case StepStatus.IN_PROGRESS:
        return 'step-list__item--in-progress';
      case StepStatus.UPCOMING:
        return 'step-list__item--upcoming';
      default:
        return '';
    }
  };

  const getStatusIcon = (status: StepItem['status'], index: number): ReactNode => {
    if (!showNumbers) {
      return status === StepStatus.UPCOMING ? (
        <span className='step-list__dot step-list__dot--upcoming'></span>
      ) : (
        <span className='step-list__dot'></span>
      );
    }

    if (status === StepStatus.COMPLETED) {
      return <span className='step-list__number step-list__number--completed'>{index + 1}</span>;
    }

    if (status === StepStatus.CURRENT) {
      return <span className='step-list__number step-list__number--current'>{index + 1}</span>;
    }

    return <span className='step-list__number step-list__number--upcoming'>{index + 1}</span>;
  };

  const getSubStepIcon = (status: SubStepStatus): ReactNode => {
    switch (status) {
      case SubStepStatus.COMPLETED:
        return (
          <SuccessIcon
            ariaLabel='Completed'
            className='step-list__substep-icon'
            size={20}
          />
        );
      case SubStepStatus.CURRENT:
        return (
          <LocationIcon
            ariaLabel='Current step'
            className='step-list__substep-icon'
            size={20}
          />
        );
      case SubStepStatus.UPCOMING:
      case SubStepStatus.ACCESSIBLE:
      default:
        return <span className='step-list__substep-dot'></span>;
    }
  };

  const handleStepClick = (step: StepItem) => {
    if (
      onStepClick &&
      (allStepsClickable ||
        step.status === StepStatus.COMPLETED ||
        step.status === StepStatus.CURRENT ||
        step.status === StepStatus.IN_PROGRESS)
    ) {
      onStepClick(step.id);
    }
  };

  const handleSubStepClick = (step: StepItem, subStep: SubStepItem, isSubStepClickable: boolean) => {
    if (!onSubStepClick || !isSubStepClickable) {
      return;
    }

    onSubStepClick(step.id, subStep.id);
  };

  if (component) {
    const StepComponent = component;

    return (
      <ol
        className={`step-list ${className}`}
        data-testid={baseTestId}
        role='list'>
        {steps.map((step, i) => (
          <div
            key={step.id || i}
            data-testid={`${baseTestId}-item-${normalizeTestId(step.id || String(i))}`}>
            <StepComponent
              completedText={completedText}
              editText={editText}
              resumeText={resumeText}
              showSubSubSteps={showSubSubSteps}
              startText={startText}
              step={{ ...step, ...{ component: component || step.component } }}
            />
          </div>
        ))}
      </ol>
    );
  }

  return (
    <ol
      aria-label='Application steps'
      className={`step-list ${className}`}
      data-testid={baseTestId}
      role='list'>
      {steps.map((step, index) => {
        const hasSubSteps = Boolean(step.subSteps && step.subSteps.length > 0);
        const isExpanded = hasSubSteps && expandedStepIds?.has(step.id);
        const isClickable =
          onStepClick &&
          (allStepsClickable ||
            step.status === StepStatus.COMPLETED ||
            step.status === StepStatus.CURRENT ||
            step.status === StepStatus.IN_PROGRESS);
        const isStepAccessible =
          allStepsClickable ||
          step.status === StepStatus.COMPLETED ||
          step.status === StepStatus.CURRENT ||
          step.status === StepStatus.IN_PROGRESS;
        const stepAllowsNavigation = !hasSubSteps && isClickable;
        const handleStepRowClick = () => {
          if (hasSubSteps) {
            if (!isStepAccessible) {
              return;
            }
            if (!isExpanded) {
              onToggleStep?.(step.id);
            }
            if (onStepClick) {
              onStepClick(step.id);
            }

            return;
          }

          if (stepAllowsNavigation) {
            handleStepClick(step);
          }
        };

        const handleCaretClick = (event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          if (hasSubSteps && isStepAccessible) {
            onToggleStep?.(step.id);
          }
        };

        return (
          <li
            key={step.id || index}
            className={classnames(
              'step-list__item',
              getStatusClass(step.status),
              isExpanded && 'step-list__item--expanded',
              !hasSubSteps && 'step-list__item--no-substeps',
              (onStepClick || hasSubSteps) && 'step-list__item--clickable',
            )}
            data-testid={`${baseTestId}-item-${normalizeTestId(step.id || String(index))}`}
            role='listitem'>
            <div
              className='step-list__content'
              data-testid={`${baseTestId}-content-${normalizeTestId(step.id || String(index))}`}>
              {stepAllowsNavigation || hasSubSteps ? (
                <button
                  aria-current={step.status === StepStatus.CURRENT ? 'step' : undefined}
                  aria-disabled={hasSubSteps && !isStepAccessible ? true : undefined}
                  aria-expanded={hasSubSteps ? isExpanded : undefined}
                  className='step-list__row-button'
                  data-testid={`${baseTestId}-row-${normalizeTestId(step.id || String(index))}`}
                  disabled={hasSubSteps && !isStepAccessible}
                  type='button'
                  onClick={handleStepRowClick}>
                  <div
                    className='step-list__indicator'
                    data-testid={`${baseTestId}-indicator-${normalizeTestId(step.id || String(index))}`}>
                    {getStatusIcon(step.status, index)}
                  </div>
                  <div
                    className='step-list__text'
                    data-testid={`${baseTestId}-text-${normalizeTestId(step.id || String(index))}`}>
                    <span
                      className='step-list__title'
                      data-testid={`${baseTestId}-title-${normalizeTestId(step.id || String(index))}`}>
                      {step.title}
                      {step.optional && <span className='step-list__optional'> (Optional)</span>}
                    </span>
                    {step.description && (
                      <p
                        className='step-list__description'
                        data-testid={`${baseTestId}-description-${normalizeTestId(step.id || String(index))}`}>
                        {step.description}
                      </p>
                    )}
                  </div>
                </button>
              ) : (
                <div
                  className='step-list__row-content'
                  data-testid={`${baseTestId}-row-${normalizeTestId(step.id || String(index))}`}>
                  <div
                    className='step-list__indicator'
                    data-testid={`${baseTestId}-indicator-${normalizeTestId(step.id || String(index))}`}>
                    {getStatusIcon(step.status, index)}
                  </div>
                  <div
                    className='step-list__text'
                    data-testid={`${baseTestId}-text-${normalizeTestId(step.id || String(index))}`}>
                    <span
                      className='step-list__title'
                      data-testid={`${baseTestId}-title-${normalizeTestId(step.id || String(index))}`}>
                      {step.title}
                      {step.optional && <span className='step-list__optional'> (Optional)</span>}
                    </span>
                    {step.description && (
                      <p
                        className='step-list__description'
                        data-testid={`${baseTestId}-description-${normalizeTestId(step.id || String(index))}`}>
                        {step.description}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {hasSubSteps && (
                <button
                  aria-disabled={!isStepAccessible ? true : undefined}
                  aria-label={isExpanded ? 'Collapse substeps' : 'Expand substeps'}
                  className='step-list__caret-button'
                  data-testid={`${baseTestId}-caret-${normalizeTestId(step.id || String(index))}`}
                  disabled={!isStepAccessible}
                  type='button'
                  onClick={handleCaretClick}>
                  <ChevronIcon direction={isExpanded ? ChevronDirection.UP : ChevronDirection.DOWN} />
                </button>
              )}
            </div>
            {hasSubSteps && isExpanded && (
              <div
                className='step-list__substeps'
                data-testid={`${baseTestId}-substeps-${normalizeTestId(step.id || String(index))}`}>
                <ol
                  className='step-list__substeps-list'
                  data-testid={`${baseTestId}-substeps-list-${normalizeTestId(step.id || String(index))}`}>
                  {step.subSteps?.map((subStep, subStepIndex) => (
                    <li
                      key={subStep.id}
                      className={`step-list__substep step-list__substep--${subStep.status}`}
                      data-testid={`${baseTestId}-substep-${normalizeTestId(
                        step.id || String(index),
                      )}-${normalizeTestId(subStep.id || String(subStepIndex))}`}>
                      {(() => {
                        const isSubStepClickable =
                          allStepsClickable ||
                          subStep.status === SubStepStatus.COMPLETED ||
                          subStep.status === SubStepStatus.CURRENT ||
                          subStep.status === SubStepStatus.ACCESSIBLE;

                        return (
                          <button
                            aria-current={subStep.status === SubStepStatus.CURRENT ? 'step' : undefined}
                            aria-disabled={!isStepAccessible ? true : undefined}
                            className='step-list__substep-button'
                            data-testid={`${baseTestId}-substep-button-${normalizeTestId(
                              step.id || String(index),
                            )}-${normalizeTestId(subStep.id || String(subStepIndex))}`}
                            disabled={!isStepAccessible || !isSubStepClickable}
                            type='button'
                            onClick={() => handleSubStepClick(step, subStep, isSubStepClickable)}>
                            <span
                              className='step-list__substep-indicator'
                              data-testid={`${baseTestId}-substep-indicator-${normalizeTestId(
                                step.id || String(index),
                              )}-${normalizeTestId(subStep.id || String(subStepIndex))}`}>
                              {getSubStepIcon(subStep.status)}
                            </span>
                            <span
                              className='step-list__substep-text'
                              data-testid={`${baseTestId}-substep-text-${normalizeTestId(
                                step.id || String(index),
                              )}-${normalizeTestId(subStep.id || String(subStepIndex))}`}>
                              {subStep.title}
                            </span>
                          </button>
                        );
                      })()}
                    </li>
                  ))}
                </ol>
                <div className='step-list__divider'></div>
              </div>
            )}
            {index < steps.length - 1 && <div className='step-list__connector'></div>}
          </li>
        );
      })}
    </ol>
  );
}

export default StepList;
